import { Package, Search, Settings, Trash, Grid, List, X, AlertCircle, CheckCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddProductForm from '../components/AddProductForm';
import {
  getProducts,
  deleteProduct,
  filterProducts,
  clearError,
  clearSuccess
} from '../redux/ProductSlice';

const Products = () => {
  const dispatch = useDispatch();
  const { filteredProducts, loading, error, success, message } = useSelector(state => state.products);

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    status: 'Active',
    description: '',
    story: '',
    topNotes: '',
    middleNotes: '',
    baseNotes: '',
    diameter: '',
    height: '',
    netWeight: '',
    burnTime: '',
    features: [],
    image: '',
    backupImage: ''
  });

  const statuses = ['Active', 'Inactive', 'Out of Stock'];

  // Fetch products on component mount
  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  // Handle filtering when search term, status, or category changes
  useEffect(() => {
    dispatch(filterProducts({ 
      searchTerm, 
      status: filterStatus,
      category: filterCategory 
    }));
  }, [searchTerm, filterStatus, filterCategory, dispatch]);

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      productToken: product.productToken,
      name: product.productName || product.name || '',
      category: product.category?.categoryid || product.categoryid || '',
      price: product.price?.toString() || '',
      stock: product.stock?.toString() || '',
      status: product.status || 'Active',
      description: product.description || '',
      story: product.story || '',
      topNotes: Array.isArray(product.primary_feature) 
        ? product.primary_feature.join(', ') 
        : product.topNotes || '',
      middleNotes: Array.isArray(product.secondary_feature)
        ? product.secondary_feature.join(', ')
        : product.middleNotes || '',
      baseNotes: Array.isArray(product.core_feature)
        ? product.core_feature.join(', ')
        : product.baseNotes || '',
      diameter: product.width || product.diameter || '',
      height: product.height || '',
      netWeight: product.weight || product.netWeight || '',
      burnTime: product.burn_duration || product.burnTime || '',
      features: product.key_feature || product.features || [],
      image: product.images?.main_image || product.image || '',
      backupImage: product.images?.backup_image || product.backupImage || '',
      otherImages: product.images?.otherImages || []
    });
    setShowModal(true);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      dispatch(deleteProduct(productToDelete.productToken));
    }
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      category: '',
      price: '',
      stock: '',
      status: 'Active',
      description: '',
      story: '',
      topNotes: '',
      middleNotes: '',
      baseNotes: '',
      diameter: '',
      height: '',
      netWeight: '',
      burnTime: '',
      features: [],
      image: '',
      backupImage: ''
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-yellow-100 text-yellow-800';
      case 'Out of Stock': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get unique categories from products for the filter dropdown
  const uniqueCategories = [...new Set(
    filteredProducts?.map(p => 
      p?.category?.category || p?.category
    ).filter(Boolean)
  )];

  // Grid Card Component
  const ProductCard = ({ product }) => {
    const [isHovered, setIsHovered] = useState(false);
    const productName = product?.productName || product?.name;
    const productCategory = product?.category?.category || product?.category;
    const mainImage = product?.images?.main_image || product?.image;
    const backupImage = product?.images?.backup_image || product?.backupImage;
    const productPrice = product?.price_th || product?.price;

    return (
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
        <div 
          className="relative h-64 bg-gray-100 overflow-hidden group"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src={isHovered && backupImage ? backupImage : mainImage}
            alt={productName}
            className="w-full h-full object-cover transition-opacity duration-500 ease-in-out"
            style={{ opacity: 1 }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {!mainImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Package size={48} className="text-gray-400" />
            </div>
          )}
          
          {/* Action buttons on hover */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex space-x-2">
            <button
              onClick={() => handleEdit(product)}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors"
              title="Edit"
            >
              <Settings size={16} className="text-blue-600" />
            </button>
            <button
              onClick={() => handleDeleteClick(product)}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
              title="Delete"
            >
              <Trash size={16} className="text-red-600" />
            </button>
          </div>

          {/* Status badge */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product?.status)}`}>
              {product?.status}
            </span>
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-1 truncate">{productName}</h3>
          <p className="text-sm text-gray-500 mb-2">{productCategory}</p>
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-amber-600">
              £{typeof productPrice === 'string' ? productPrice : productPrice?.toFixed(2)}
            </span>
            <span className="text-sm text-gray-600">
              Stock: {product?.stock}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Success/Error Notification */}
      {(success || error) && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center space-x-3">
            {success ? (
              <CheckCircle className="text-green-600" size={20} />
            ) : (
              <AlertCircle className="text-red-600" size={20} />
            )}
            <span className={success ? 'text-green-800' : 'text-red-800'}>
              {success ? message : error}
            </span>
          </div>
          <button
            onClick={() => {
              dispatch(clearSuccess());
              dispatch(clearError());
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
          <p className="text-gray-500 text-sm">Manage your store's products</p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
        >
          <Package size={20} />
          <span>Add Product</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full"
              />
            </div>
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'table' 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <List size={18} />
              <span className="hidden sm:inline">Table</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 flex items-center space-x-2 transition-colors border-l ${
                viewMode === 'grid' 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Grid size={18} />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>
        </div>
      </div>

      {/* Products Display */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            All Products ({filteredProducts?.length || 0})
          </h3>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading products...</p>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts?.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Product</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Category</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Price</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Stock</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Status</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Date Added</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts?.map((product) => {
                    const productName = product?.productName || product?.name;
                    const productCategory = product?.category?.category || product?.category;
                    const productImage = product?.images?.main_image || product?.image;
                    const productPrice = product?.price_th || product?.price;
                    const dateAdded = product?.created_at ? new Date(product?.created_at).toLocaleDateString() : product?.dateAdded;

                    return (
                      <tr key={product.id} className="border-b border-gray-50">
                        <td className="py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                              {productImage ? (
                                <img
                                  src={productImage}
                                  alt={productName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Package size={20} className="text-gray-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{productName}</p>
                              {product?.description && (
                                <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                                  {product?.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-sm text-gray-600">{productCategory}</td>
                        <td className="py-4 text-sm font-medium text-gray-900">
                          £{typeof productPrice === 'string' ? productPrice : productPrice?.toFixed(2)}
                        </td>
                        <td className="py-4 text-sm text-gray-600">{product?.stock}</td>
                        <td className="py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product?.status)}`}>
                            {product?.status}
                          </span>
                        </td>
                        <td className="py-4 text-sm text-gray-600">{dateAdded}</td>
                        <td className="py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(product)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Settings size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(product)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filteredProducts?.length === 0 && !loading && (
            <div className="text-center py-12">
              <Package size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Product</h3>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{productToDelete?.productName || productToDelete?.name}"? This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <AddProductForm
            setShowModal={setShowModal}
            setFormData={setFormData}
            formData={formData}
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
          />
        </div>
      )}
    </div>
  );
};

export default Products;